import React, { useState, useEffect } from 'react';
import Img from './Img';
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { track } from '../utils/analytics';
import work from '../data/work';

// One size on every page. It used to shrink on scroll, which meant the
// homepage (where the bar only appears after the hero) showed a smaller logo
// than every other page.
const ShrinkingLogo = () => (
    <Img
        src="/media/minneai-logo.webp"
        width={800} height={214}
        alt="minneAI Logo"
        sx={{ maxWidth: { xs: 140, md: 170 }, height: 'auto', display: 'block' }}
    />
);

// Homepage sections the nav tracks, in document order.
const SECTION_IDS = ['services', 'story', 'work', 'about'];

// Leave modified clicks (new tab, new window) to the browser; the links are real.
const isPlainClick = (e) =>
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const navLinkSx = {
    fontWeight: 500,
    textTransform: 'lowercase',
    letterSpacing: '0.07em',
    fontSize: '0.95rem',
    color: 'text.primary',
    px: 1.5,
    py: 0.5,
    minWidth: 0,
    position: 'relative',
    '&.Mui-focusVisible': { outline: `2px solid ${colors.green}`, outlineOffset: 2 },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 4,
        left: 12,
        right: 12,
        height: '2px',
        backgroundColor: colors.green,
        transform: 'scaleX(0)',
        transformOrigin: 'center',
        transition: 'transform 0.22s ease',
        borderRadius: '2px',
    },
    '&:hover::after': {
        transform: 'scaleX(1)',
    },
    '&:hover': {
        color: colors.green,
        backgroundColor: 'transparent',
    },
};

const BasicMenu = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pastHero, setPastHero] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/';

    const [activeSection, setActiveSection] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            setPastHero(window.scrollY > 50);

            if (location.pathname !== '/') return;

            if (window.scrollY < window.innerHeight * 0.5) {
                setActiveSection('home');
                return;
            }

            // Whichever section straddles the line just under the app bar wins.
            const marker = 150;
            const current = SECTION_IDS.find((id) => {
                const el = document.getElementById(id);
                if (!el) return false;
                const rect = el.getBoundingClientRect();
                return rect.top <= marker && rect.bottom > marker;
            });
            setActiveSection(current || null);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    const hideMenu = isHome && !pastHero;

    const SECTION_LABELS = { home: 'Home', services: 'Services', story: 'How we work', work: 'Work', about: 'About' };

    const getActiveLabel = () => {
        if (work.some((p) => location.pathname === `/work/${p.id}`)) return 'Work';
        if (!isHome) return location.pathname.replace('/', '');
        return SECTION_LABELS[activeSection] || null;
    };

    const activeLabelNow = getActiveLabel();

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const menuItems = [
        { label: 'Home', path: '/' },
        { label: 'Services', scrollToId: 'services' },
        { label: 'How we work', scrollToId: 'story' },
        { label: 'Work', scrollToId: 'work' },
        { label: 'About', scrollToId: 'about' },
    ];

    const handleNav = (item, source = 'header') => {
        track('nav_click', { item: item.scrollToId || item.path, source });
        if (item.scrollToId) {
            if (window.location.pathname === '/') {
                const el = document.getElementById(item.scrollToId);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            } else {
                navigate('/#' + item.scrollToId);
            }
        } else if (window.location.pathname === item.path) {
            // Already here: just glide back to the top.
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Route change: AppRoutes resets the scroll once the old page has
            // faded out. Scrolling here too made the outgoing page slide up
            // mid-fade, which read as a glitch.
            navigate(item.path);
        }
    };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    transform: hideMenu ? 'translateY(-100%)' : 'translateY(0)',
                    // Hidden for keyboard and screen readers too, not just slid off-screen.
                    visibility: hideMenu ? 'hidden' : 'visible',
                    transition: 'transform 0.35s ease, visibility 0.35s',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, minHeight: { xs: 56, md: 64 } }}>
                    {/* Logo */}
                    <Box
                        component={RouterLink}
                        to="/"
                        aria-label="minneAI home"
                        onClick={(e) => {
                            // Already home: the router has nothing to do, so scroll instead.
                            if (location.pathname === '/' && isPlainClick(e)) {
                                e.preventDefault();
                                track('nav_click', { item: 'logo' });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer',
                              '&:focus-visible': { outline: `2px solid ${colors.green}`, outlineOffset: 4, borderRadius: 1 } }}
                    >
                        <ShrinkingLogo />
                    </Box>

                    {/* Desktop nav */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                        {menuItems.map((item) => {
                            const isActive = item.label === activeLabelNow ||
                                (item.path && item.path.replace('/', '') === activeLabelNow);
                            return (
                                <Button
                                    key={item.label}
                                    component={RouterLink}
                                    to={item.path || `/#${item.scrollToId}`}
                                    onClick={(e) => { if (isPlainClick(e)) { e.preventDefault(); handleNav(item); } }}
                                    variant="text"
                                    disableRipple
                                    sx={{
                                        ...navLinkSx,
                                        color: isActive ? colors.greenText : 'text.primary',
                                        '&::after': {
                                            ...navLinkSx['&::after'],
                                            transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                                        },
                                    }}
                                >
                                    {item.label}
                                </Button>
                            );
                        })}
                        <Button
                            component={RouterLink}
                            to="/Contact"
                            onClick={() => track('cta_click', { location: 'nav' })}
                            variant="contained"
                            disableElevation
                            sx={{
                                ml: 2,
                                backgroundColor: colors.green,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                px: 2.5,
                                py: 0.8,
                                borderRadius: '20px',
                                textTransform: 'none',
                                letterSpacing: '0.02em',
                                '&.Mui-focusVisible': { outline: `2px solid ${colors.green}`, outlineOffset: 3 },
                                '&:hover': {
                                    backgroundColor: colors.greenDark,
                                    boxShadow: '0 2px 14px rgba(22,163,74,0.32)',
                                },
                            }}
                        >
                            Let's Chat
                        </Button>
                    </Box>

                    {/* Mobile: compact CTA beside the hamburger, so it isn't buried in the drawer */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
                        <Button
                            component={RouterLink}
                            to="/Contact"
                            onClick={() => track('cta_click', { location: 'nav_mobile' })}
                            variant="contained"
                            disableElevation
                            size="small"
                            sx={{
                                backgroundColor: colors.green, color: 'white', fontWeight: 600,
                                fontSize: '0.8rem', px: 1.75, py: 0.6, borderRadius: '20px', textTransform: 'none',
                                whiteSpace: 'nowrap', minWidth: 0,
                                '&:hover': { backgroundColor: colors.greenDark },
                            }}
                        >
                            Let's Chat
                        </Button>
                        <IconButton size="large" onClick={handleDrawerToggle} edge="end" aria-label="Open menu">
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle}
                PaperProps={{ sx: { width: 260, pt: 2 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pb: 2 }}>
                    <Img
                        src="/media/minneai-logo.webp"
                        width={800} height={214}
                        alt="minneAI Logo"
                        sx={{ maxWidth: 140, height: 'auto' }}
                    />
                    <IconButton onClick={handleDrawerToggle} size="small" aria-label="Close menu">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <List sx={{ px: 1, pt: 1 }}>
                    {menuItems.map((item) => (
                        <ListItemButton
                            key={item.label}
                            component={RouterLink}
                            to={item.path || `/#${item.scrollToId}`}
                            onClick={(e) => { if (isPlainClick(e)) { e.preventDefault(); handleNav(item, 'drawer'); handleDrawerToggle(); } }}
                            sx={{
                                borderRadius: 2,
                                mb: 0.5,
                                '&:hover': { backgroundColor: 'rgba(22,163,74,0.08)' },
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    textTransform: 'lowercase',
                                    letterSpacing: '0.07em',
                                    color: 'text.primary',
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
                <Box sx={{ px: 2.5, pt: 2 }}>
                    <Button
                        component={RouterLink}
                        to="/Contact"
                        onClick={() => { track('cta_click', { location: 'drawer' }); handleDrawerToggle(); }}
                        variant="contained"
                        fullWidth
                        disableElevation
                        sx={{
                            backgroundColor: colors.green,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            py: 1.2,
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: colors.greenDark },
                        }}
                    >
                        Let's Chat
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

export default BasicMenu;
